import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Feature,
  OrderProductPreview,
  PlanInterval,
  Subscription,
} from '../../../../models';
import { fromPromotion } from '../../../../store';
import { formatCurrency } from '../../../../components';
import { TrackingPayload } from '../../../../utils/utils';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../../../utils/Constants';
import {
  getPreviewExistingQuantity,
  getPaidAddOnLineItems,
  getPreviewProductFeature,
  getTrueBundleTitleId,
  isMonthlyToAnnualSwitchPreview,
} from './trueBundlePreview';
import { getStarterPackageSummaryLine } from './starterPackageCampaign';

const useStyles = makeStyles(theme => ({
  productRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.4, 0),
  },
  productBlock: {
    borderBottom: '1px solid #F0EFF6',
  },
  productName: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.35,
    color: '#22283c',
  },
  productQuantity: {
    fontSize: 13,
    lineHeight: 1.45,
    color: '#9CA3AF',
    marginTop: theme.spacing(0.25),
  },
  productAmount: {
    fontWeight: 800,
    fontSize: 15,
    lineHeight: 1.35,
    color: '#22283c',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  productOriginalAmount: {
    fontSize: 12.5,
    color: '#9CA3AF',
    textDecoration: 'line-through',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  productTaxAmount: {
    fontSize: 12.5,
    color: '#808B9A',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  promotionNote: {
    backgroundColor: '#ECFDF5',
    border: '1px solid #A7F3D0',
    borderRadius: 8,
    color: '#047857',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.45,
    margin: theme.spacing(0.25, 0, 0.75),
    padding: theme.spacing(1, 1.25),
  },
  promotionNoteContent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  promotionNoteInfoIcon: {
    color: '#047857',
    cursor: 'help',
    fontSize: 16,
    flexShrink: 0,
  },
  lightTooltip: {
    maxWidth: 360,
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: '#ffffff',
    color: '#374151',
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.45,
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
  },
  lightTooltipArrow: {
    color: '#ffffff',
  },
  billedPill: {
    display: 'inline-block',
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '.08em',
    color: '#16141d',
    textTransform: 'uppercase',
    padding: theme.spacing(0.375, 1.125),
    borderRadius: 999,
    marginBottom: theme.spacing(1),
  },
  billedPillYear: {
    backgroundColor: '#dcf1e8',
  },
  billedPillMonth: {
    backgroundColor: '#ecebfb',
  },
  starterLine: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(0.75),
    fontSize: 12,
    lineHeight: 1.45,
    color: '#5b6661',
  },
  starterLineLabel: {
    minWidth: 0,
    display: 'inline-flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: theme.spacing(0.625),
  },
  starterLineTag: {
    fontSize: 12,
    fontWeight: 600,
    color: '#8a7218',
  },
  starterLineAmount: {
    whiteSpace: 'nowrap',
    color: '#3c3a50',
  },
}));

export const getSummaryUnitWord = (
  feature: Feature,
  quantity: number,
) => {
  if (feature === Feature.PER_USER_KSE || feature === Feature.UNLIMITED_ENGINE) {
    return quantity === 1 ? 'license' : 'licenses';
  }

  if (
    feature === Feature.TESTCLOUD_SESSION_CROSS_BROWSER
    || feature === Feature.TESTCLOUD_SESSION_MOBILE_APP
    || feature === Feature.TESTCLOUD_SESSION_PRIVATE_DEVICE
    || feature === Feature.TESTCLOUD_SESSION_WEB_DESKTOP
  ) {
    return quantity === 1 ? 'session' : 'sessions';
  }

  return quantity === 1 ? 'seat' : 'seats';
};

export const getSummaryQuantityLabel = (
  feature: Feature,
  quantity: number,
) => `${quantity} ${getSummaryUnitWord(feature, quantity)}`;

interface OrderSummaryLineItemsProps {
  selectedProducts: OrderProductPreview[];
  currentSubscriptions: Subscription[];
  groupByInterval?: boolean;
  onPromotionTooltipOpen?: (payload: TrackingPayload) => void;
}

export const OrderSummaryLineItems = ({
  selectedProducts,
  currentSubscriptions,
  groupByInterval = false,
  onPromotionTooltipOpen,
}: OrderSummaryLineItemsProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const activePromotion = useSelector(fromPromotion.selectActivePromotion);

  const getProductInterval = (product: OrderProductPreview): PlanInterval => product.billingInterval
    ?? PlanInterval.getIntervalFromPlanCode(product.productCode);

  const suffixForInterval = (interval: PlanInterval) => intl.formatMessage({
    id: interval === PlanInterval.YEAR
      ? 'page.plan.v2.true.testing.price.year'
      : 'page.plan.v2.true.testing.price.month',
  });

  const formatPeriodPriceForInterval = (amount: number, interval: PlanInterval) => (
    `${formatCurrency(intl, amount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}${suffixForInterval(interval)}`
  );

  const selectedProductRows = selectedProducts.flatMap(product => {
    const isSwitchToAnnual = isMonthlyToAnnualSwitchPreview(product, currentSubscriptions);
    const billingInterval = getProductInterval(product);
    const feature = getPreviewProductFeature(product);
    const quantity = isSwitchToAnnual ? product.quantity : product.newQuantity;
    // Starter-package rows swap the generic promotion note for the package breakdown
    // lines of the Billing 2.0 design.
    const paidAddOns = getPaidAddOnLineItems([product]);
    const paidAddOnSubTotal = paidAddOns.reduce((sum, item) => sum + item.subtotal, 0);
    const paidAddOnDiscount = paidAddOns.reduce((sum, item) => sum + item.discountAmount, 0);
    const paidAddOnTax = paidAddOns.reduce((sum, item) => sum + item.tax, 0);
    const productSubTotal = Math.max(product.subTotal - paidAddOnSubTotal, 0);
    const productDiscountAmount = Math.max(product.discountAmount - paidAddOnDiscount, 0);
    const starterLine = getStarterPackageSummaryLine(
      activePromotion,
      product.appliedPromotion,
      quantity,
      productSubTotal,
    );

    return [{
      key: product.productCode,
      feature,
      billingInterval,
      titleId: getTrueBundleTitleId(feature),
      quantity,
      total: product.total,
      subTotal: productSubTotal,
      discountAmount: productDiscountAmount,
      tax: Math.max(product.tax - paidAddOnTax, 0),
      isSwitchToAnnual,
      existingQuantity: isSwitchToAnnual ? getPreviewExistingQuantity(product) : 0,
      newQuantity: product.newQuantity,
      starterLine,
      showPromotionNote: !starterLine
        && feature === Feature.TRUE_AUTOMATION
        && product.billingInterval === PlanInterval.YEAR
        && (product.discountAmount > 0
          || (product.appliedPromotion?.discountAmount ?? 0) > 0),
      promotionDiscountAmount: product.appliedPromotion?.discountAmount ?? product.discountAmount,
      promotionDiscountQuantity: product.appliedPromotion?.discountQuantity
        ?? product.discountQuantity,
    }];
  });

  const renderProductRow = (product: (typeof selectedProductRows)[number]) => {
    const productAmountBeforeTax = Math.max(
      (product.subTotal ?? 0) - (product.discountAmount ?? 0),
      0,
    );
    const promotionDiscountQuantity = product.promotionDiscountQuantity
      || product.quantity;

    return (
      <Box key={product.key} className={classes.productBlock}>
        <Box className={classes.productRow}>
          <Box minWidth={0}>
            <Typography className={classes.productName}>
              <FormattedMessage id={product.titleId} />
            </Typography>
            <Typography className={classes.productQuantity}>
              {product.isSwitchToAnnual
                ? intl.formatMessage(
                  {
                    id: 'page.plan.v2.order.summary.seats.total.value',
                  },
                  {
                    count: product.quantity,
                  },
                )
                : getSummaryQuantityLabel(product.feature, product.quantity)}
            </Typography>
            {product.isSwitchToAnnual && (
              <Typography className={classes.productQuantity}>
                <FormattedMessage
                  id={product.newQuantity > 0
                    ? 'page.plan.v2.order.summary.includes.current.new.seats'
                    : 'page.plan.v2.order.summary.includes.current.seats'}
                  values={{
                    currentCount: product.existingQuantity,
                    newCount: product.newQuantity,
                  }}
                />
              </Typography>
            )}
          </Box>
          {product.total !== undefined && (
            <Box>
              <Typography className={classes.productAmount}>
                {formatPeriodPriceForInterval(productAmountBeforeTax, product.billingInterval)}
              </Typography>
              {!product.starterLine && (product.subTotal ?? 0) > productAmountBeforeTax && (
                <Typography className={classes.productOriginalAmount}>
                  {formatPeriodPriceForInterval(product.subTotal ?? 0, product.billingInterval)}
                </Typography>
              )}
              {(product.tax ?? 0) > 0 && (
                <Typography className={classes.productTaxAmount}>
                  <FormattedMessage
                    id="page.plan.v2.order.summary.product.tax"
                    values={{
                      amount: formatCurrency(
                        intl,
                        product.tax,
                        DEFAULT_CURRENCY_FORMAT_OPTIONS,
                      ),
                    }}
                  />
                </Typography>
              )}
            </Box>
          )}
        </Box>
        {product.starterLine && (
          <Box pb={1}>
            <Box className={classes.starterLine}>
              <Box component="span" className={classes.starterLineLabel}>
                <Typography component="span" className={classes.starterLineTag}>
                  <FormattedMessage id="page.plan.v2.order.summary.starter.tag" />
                </Typography>
                {/* Tiered offers name the discounted per-seat rate ("· 3 licenses at
                    $1,000/yr" / "· first 3 licenses"); one-time offers describe the
                    package ("· 5 seats included" / "· 5 seats"). */}
                {product.starterLine.kind === 'tiered' ? (
                  <FormattedMessage
                    id={product.starterLine.additionalSeats > 0
                      ? 'page.plan.v2.order.summary.starter.seats.first'
                      : 'page.plan.v2.order.summary.starter.seats.at'}
                    values={{
                      count: product.starterLine.packageSeats,
                      unit: getSummaryUnitWord(
                        product.feature,
                        product.starterLine.packageSeats,
                      ),
                      price: formatCurrency(
                        intl,
                        product.starterLine.packageUnitPrice,
                        DEFAULT_CURRENCY_FORMAT_OPTIONS,
                      ),
                      interval: suffixForInterval(product.billingInterval),
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id={product.starterLine.additionalSeats > 0
                      ? 'page.plan.v2.order.summary.starter.seats'
                      : 'page.plan.v2.order.summary.starter.seats.included'}
                    values={{
                      count: product.starterLine.packageSeats,
                      unit: getSummaryUnitWord(
                        product.feature,
                        product.starterLine.packageSeats,
                      ),
                    }}
                  />
                )}
              </Box>
              {product.starterLine.additionalSeats > 0 && (
                <Typography component="span" className={classes.starterLineAmount}>
                  {formatCurrency(
                    intl,
                    product.starterLine.packagePrice,
                    DEFAULT_CURRENCY_FORMAT_OPTIONS,
                  )}
                </Typography>
              )}
            </Box>
            {product.starterLine.additionalSeats > 0 && (
              <Box className={classes.starterLine}>
                <Box component="span" className={classes.starterLineLabel}>
                  <FormattedMessage
                    id="page.plan.v2.order.summary.starter.additional"
                    values={{
                      count: product.starterLine.additionalSeats,
                      unit: getSummaryUnitWord(
                        product.feature,
                        product.starterLine.additionalSeats,
                      ),
                      price: formatCurrency(
                        intl,
                        product.starterLine.additionalUnitPrice,
                        DEFAULT_CURRENCY_FORMAT_OPTIONS,
                      ),
                      interval: suffixForInterval(product.billingInterval),
                    }}
                  />
                </Box>
                <Typography component="span" className={classes.starterLineAmount}>
                  {formatCurrency(
                    intl,
                    product.starterLine.additionalPrice,
                    DEFAULT_CURRENCY_FORMAT_OPTIONS,
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {product.showPromotionNote && (
          <Box className={classes.promotionNote}>
            <Box component="span" className={classes.promotionNoteContent}>
              <FormattedMessage
                id="page.plan.v2.order.summary.true.automation.promotion.note"
                values={{
                  amount: formatCurrency(
                    intl,
                    product.promotionDiscountAmount,
                    DEFAULT_CURRENCY_FORMAT_OPTIONS,
                  ),
                  count: promotionDiscountQuantity,
                }}
              />
              <Tooltip
                arrow
                placement="top"
                classes={{
                  tooltip: classes.lightTooltip,
                  arrow: classes.lightTooltipArrow,
                }}
                onOpen={() => onPromotionTooltipOpen?.({
                  field: 'order_summary',
                  object: 'true_automation_promotion_tooltip',
                  action: 'view_tooltip',
                  promotion_discount_amount: product.promotionDiscountAmount,
                  promotion_discount_quantity: promotionDiscountQuantity,
                })}
                title={(
                  <FormattedMessage
                    id="page.plan.v2.order.summary.true.automation.promotion.tooltip"
                    values={{
                      amount: formatCurrency(
                        intl,
                        product.promotionDiscountAmount,
                        DEFAULT_CURRENCY_FORMAT_OPTIONS,
                      ),
                      count: promotionDiscountQuantity,
                    }}
                  />
                )}
              >
                <InfoOutlinedIcon className={classes.promotionNoteInfoIcon} />
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box mt={groupByInterval ? 0 : 1.5}>
      {groupByInterval
        ? [PlanInterval.YEAR, PlanInterval.MONTH]
          .filter(interval => selectedProductRows.some(
            row => row.billingInterval === interval,
          ))
          .map((interval, intervalIndex) => (
            <Box key={interval} mt={intervalIndex === 0 ? 0 : 1.75}>
              <Typography
                className={`${classes.billedPill} ${interval === PlanInterval.YEAR
                  ? classes.billedPillYear
                  : classes.billedPillMonth}`}
              >
                <FormattedMessage
                  id={interval === PlanInterval.YEAR
                    ? 'page.plan.v2.order.summary.billed.annually'
                    : 'page.plan.v2.order.summary.billed.monthly'}
                />
              </Typography>
              {selectedProductRows
                .filter(row => row.billingInterval === interval)
                .map(renderProductRow)}
            </Box>
          ))
        : selectedProductRows.map(renderProductRow)}
    </Box>
  );
};
